import { isDevToolsEnabled } from "@/config/constants";
import {
  Organization,
  ORGANIZATION_TYPE,
  OrganizationType,
} from "@/types/organization";
import { Access, PermissionCatalog } from "@/types/role";
import { ORG_ROLE, User, UserWorkspace, WORKSPACE_ROLE } from "@/types/user";

export const DEV_ROLE = {
  ADMIN: "admin",
  MEMBER: "member",
  AGENT: "agent",
} as const;

export type DevRole = (typeof DEV_ROLE)[keyof typeof DEV_ROLE];

export const DEV_ROLE_OPTIONS: { key: DevRole; label: string }[] = [
  { key: DEV_ROLE.ADMIN, label: "Admin" },
  { key: DEV_ROLE.MEMBER, label: "Member" },
  { key: DEV_ROLE.AGENT, label: "Agent" },
];

/**
 * Module-level so it survives the bootstrap `users` refetch (which re-pushes
 * the real user through the store) but not a reload. `isDevToolsEnabled` is
 * false in production, so the override can never leak there.
 */
let devRoleOverride: DevRole | null = null;

export const getDevRoleOverride = (): DevRole | null =>
  isDevToolsEnabled() ? devRoleOverride : null;

export const setDevRoleOverride = (role: DevRole): void => {
  devRoleOverride = role;
};

/**
 * Collapse the org/workspace role pair the app actually gates on into the
 * single "admin | member | agent" knob the tweaks sidebar exposes.
 */
export const deriveDevRole = (
  isOrgAdmin: boolean,
  isWorkspaceAgent: boolean,
): DevRole => {
  if (isOrgAdmin) {
    return DEV_ROLE.ADMIN;
  }
  if (isWorkspaceAgent) {
    return DEV_ROLE.AGENT;
  }

  return DEV_ROLE.MEMBER;
};

/**
 * Project the chosen tweak role back onto the logged-in user so `useRoles`
 * (org role + active-workspace role) resolves to the expected permissions:
 * - admin  → org admin (full access regardless of workspace role)
 * - member → org member + workspace member
 * - agent  → org member + workspace agent
 */
export const applyDevRole = (
  user: User,
  role: DevRole,
  activeWorkspaceId: string | null,
  activeWorkspaceName: string,
): User => {
  if (role === DEV_ROLE.ADMIN) {
    return { ...user, role: ORG_ROLE.ADMIN };
  }

  const workspaceRole =
    role === DEV_ROLE.AGENT ? WORKSPACE_ROLE.AGENT : WORKSPACE_ROLE.MEMBER;

  const existing = user.workspaces ?? [];

  let workspaces: UserWorkspace[];

  if (activeWorkspaceId == null) {
    workspaces = existing;
  } else if (existing.some((workspace) => workspace.id === activeWorkspaceId)) {
    workspaces = existing.map((workspace) =>
      workspace.id === activeWorkspaceId
        ? { ...workspace, role: workspaceRole }
        : workspace,
    );
  } else {
    workspaces = [
      ...existing,
      { id: activeWorkspaceId, name: activeWorkspaceName, role: workspaceRole },
    ];
  }

  return { ...user, role: ORG_ROLE.MEMBER, workspaces };
};

/**
 * The same projection onto `/users/me` access, which is what `useRoles` reads
 * first. A Member or an Agent holds what that role type starts with.
 */
export const applyDevAccess = (
  access: Access,
  role: DevRole,
  presets: PermissionCatalog["presets"],
): Access => {
  if (role === DEV_ROLE.ADMIN) {
    return { ...access, organizationRole: ORG_ROLE.ADMIN };
  }

  const base =
    role === DEV_ROLE.AGENT ? WORKSPACE_ROLE.AGENT : WORKSPACE_ROLE.MEMBER;

  return {
    ...access,
    organizationRole: ORG_ROLE.MEMBER,
    base,
    permissions: presets[base] ?? [],
  };
};

export const DEV_ORG_TYPE_OPTIONS: {
  key: OrganizationType;
  label: string;
}[] = [
  { key: ORGANIZATION_TYPE.CLINIC, label: "Clinic" },
  { key: ORGANIZATION_TYPE.ECOMMERCE, label: "E-commerce" },
];

/** Same lifetime as `devRoleOverride`: survives refetches, not a reload. */
let devOrgTypeOverride: OrganizationType | null = null;

export const setDevOrgTypeOverride = (type: OrganizationType): void => {
  devOrgTypeOverride = type;
};

/**
 * Applied where the organization is fetched, so the store and every direct
 * `useGetOrganization` consumer see the same type. Off in production.
 */
export const applyDevOrgType = <T extends Organization | null>(
  organization: T,
): T =>
  isDevToolsEnabled() && devOrgTypeOverride && organization
    ? { ...organization, type: devOrgTypeOverride }
    : organization;
